import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzBtwDataService,
  BtwOverzicht,
  ICP,
  IcpAangifte,
  ICPLandcode,
  ICPSoort,
  ValueAddedTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {debounceTime, filter, merge, Observable, shareReplay, tap} from 'rxjs';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule, DatePipe} from '@angular/common';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-aangifte-icp',
    templateUrl: './aangifte-icp.component.html',
    styleUrls: ['./aangifte-icp.component.scss'],
    imports: [
        SharedUiAlertModule,
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        DatePipe,
        SharedUiButtonModule,
        CommonModule
    ]
})
export class AangifteIcpComponent implements OnInit, OnDestroy {

  @Input() public suppletie = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzBtwDataService);

  public vatEntity = {} as ValueAddedTaxEntity;
  public index = 0;
  public telefoonnummer = '0612345678';

  public icpOpgaafFg = new FormGroup({
    opgaves: new FormArray<FormGroup>([])
  });

  public optionsLanden: SelectFormControlItem<string>[] = [
    {value: 'BE', label: 'België'},
    {value: 'BG', label: 'Bulgarije'},
    {value: 'CY', label: 'Cyprus'},
    {value: 'DE', label: 'Duitsland'},
    {value: 'DK', label: 'Denemarken'},
    {value: 'EE', label: 'Estland'},
    {value: 'EL', label: 'Griekenland'},
    {value: 'FI', label: 'Finland'},
    {value: 'FR', label: 'Frankrijk'},
    {value: 'HU', label: 'Hongarije'},
    {value: 'IE', label: 'Ierland'},
    {value: 'IT', label: 'Italië'},
    {value: 'HR', label: 'Kroatië'},
    {value: 'LV', label: 'Letland'},
    {value: 'LT', label: 'Litouwen'},
    {value: 'LU', label: 'Luxemburg'},
    {value: 'MT', label: 'Malta'},
    {value: 'AT', label: 'Oostenrijk'},
    {value: 'PL', label: 'Polen'},
    {value: 'PT', label: 'Portugal'},
    {value: 'RO', label: 'Roemenië'},
    {value: 'SI', label: 'Slovenië'},
    {value: 'SK', label: 'Slowakije'},
    {value: 'ES', label: 'Spanje'},
    {value: 'CZ', label: 'Tjechië'},
    {value: 'SE', label: 'Zweden'},
  ];

  public optionsICP: SelectFormControlItem<string>[] = [
    {value: 'INTRACOMMUNAUTAIRE_LEVERING', label: 'Levering'},
    {value: 'INTRACOMMUNAUTAIRE_DIENST', label: 'Dienst'},
    {value: 'INTRACOMMUNAUTAIRE_ABC_LEVERING', label: 'ABC-levering'},
    {value: 'CORRECTIE_INTRACOMMUNAUTAIRE_LEVERING', label: 'Correctie levering'},
    {value: 'CORRECTIE_INTRACOMMUNAUTAIRE_DIENST', label: 'Correctie dienst'},
    {value: 'CORRECTIE_INTRACOMMUNAUTAIRE_ABC_LEVERING', label: 'Correctie ABC-levering'}
  ];

  readonly valueAddedTaxEntity$: Observable<ValueAddedTaxEntity> =
    this.dataService.valueAddedTaxEntity.pipe(
      filter((data): data is ValueAddedTaxEntity => !!data?.overzicht),
      tap(value => {
        this.telefoonnummer = value.overzicht?.telefoonnummer ?? '0612345678';
        this.updateForm(value);
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly periodeIndex$: Observable<number> =
    this.dataService.periodeIndex.pipe(
      tap(value => {
        this.index = value;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.periodeIndex$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    merge(this.icpOpgaafFg.valueChanges).pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  createFormGroup(item?: IcpAangifte): FormGroup {
    return new FormGroup({
      initialenBelastingplichtige: new FormControl<string | null>(item?.initialenBelastingplichtige ?? null),
      tussenvoegselBelastingplichtige: new FormControl<string | null>(item?.tussenvoegselBelastingplichtige ?? null),
      achternaamBelastingplichtige: new FormControl<string | null>(item?.achternaamBelastingplichtige ?? null),
      omzetbelastingNummer: new FormControl<string | null>(item?.omzetbelastingNummer ?? null),
      beconnummer: new FormControl<string | null>(item?.beconnummer ?? null),
      naamContactPersoon: new FormControl<string | null>(item?.naamContactPersoon ?? null),
      telefoonnummer: new FormControl<string | null>(item?.telefoonnummer ?? this.telefoonnummer),
      tijdvak: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.tijdvak?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.tijdvak?.einddatum ?? null)
      }),
      icps: new FormArray<FormGroup>([])
    });
  }

  createIcpItem(item?: ICP): FormGroup {
    return new FormGroup({
      landcode: new FormControl<ICPLandcode | null>(item?.landcode ?? null, {validators: [Validators.required]}),
      nationaalBtwIdNummer: new FormControl<string | null>(
        item?.nationaalBtwIdNummer ?? null,
        {validators: [Validators.required, Validators.pattern(/^[0-9A-Z]{1,12}$/)]}
      ),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null, {validators: [Validators.required]}),
      soort: new FormControl<ICPSoort | null>(item?.soort ?? null, {validators: [Validators.required]}),
      tijdvakCorrectie: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.tijdvakCorrectie?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.tijdvakCorrectie?.einddatum ?? null)
      })
    });
  }

  get opgaves(): FormArray<FormGroup> {
    return this.icpOpgaafFg.get('opgaves') as FormArray<FormGroup>;
  }

  getIcp(index: number): FormArray<FormGroup> {
    const outerGroup = this.opgaves.at(index) as FormGroup;
    return outerGroup.get('icps') as FormArray<FormGroup>;
  }

  addIcp(index: number): void {
    const innerArray = this.getIcp(index);
    innerArray.push(this.createIcpItem());
  }

  deleteIcp(outerIndex: number, innerIndex: number): void {
    const innerArray = this.getIcp(outerIndex);
    innerArray.removeAt(innerIndex);
    this.saveForm();
  }

  private updateForm(value: ValueAddedTaxEntity): void {
    this.vatEntity = value;

    const lijst = value.overzicht?.icpAangiftes || [];
    const formArray = this.opgaves;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      const row = {
        ...lijst[i],
        telefoonnummer: lijst[i].telefoonnummer ?? this.telefoonnummer,
      };

      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(row, {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(row), {emitEvent: false});
      }
    }

    for (let i = 0; i < lijst.length; i++) {
      const opgave = lijst[i];
      const icps = opgave.icps || [];
      const icpFormArray = this.getIcp(i);

      while (icpFormArray.length > icps.length) {
        icpFormArray.removeAt(icpFormArray.length - 1, {emitEvent: false});
      }

      for (let j = 0; j < icps.length; j++) {
        if (j < icpFormArray.length) {
          const control = icpFormArray.at(j) as FormGroup;
          control.patchValue(icps[j], {emitEvent: false});
        } else {
          icpFormArray.push(this.createIcpItem(icps[j]), {emitEvent: false});
        }
      }
    }
  }

  saveForm(): void {
    this.icpOpgaafFg.updateValueAndValidity({emitEvent: false});
    const icpAangiftes = (this.icpOpgaafFg.getRawValue().opgaves ?? []) as IcpAangifte[];

    this.dataService.patchValueAddedTaxEntity(current => {
      if (!current.overzicht) {
        return current;
      }

      return {
        ...current,
        overzicht: {
          ...current.overzicht,
          icpAangiftes,
        } as BtwOverzicht
      } as ValueAddedTaxEntity;
    });
  }
}
